import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronDown, KeyRound, LogOut, UserCircle } from 'lucide-react'
import { useAuth } from '../../store/auth'
import { AccountProfileModal, ChangePasswordModal } from '../AccountProfileModal'

// TailAdmin UserDropdown: 44px avatar + name + chevron; a 260px rounded-2xl panel with name/email,
// an item list, and a separate sign-out row. Replaces the four copy-pasted user menus that used to
// live in each workspace shell.
export function UserMenu({ showProfile = true }: { showProfile?: boolean }) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  const [changePwOpen, setChangePwOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey) }
  }, [open])

  const initials = user?.fullName?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() ?? '?'
  const firstName = user?.fullName?.split(' ')[0]

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center text-gray-700"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="mr-3 flex h-11 w-11 items-center justify-center overflow-hidden rounded-full bg-brand-50 text-sm font-semibold text-brand-500">
          {initials}
        </span>
        <span className="mr-1 hidden text-theme-sm font-medium sm:block">{firstName}</span>
        <ChevronDown size={18} className={`text-gray-500 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 mt-[17px] flex w-[260px] flex-col rounded-2xl border border-gray-200 bg-white p-3 shadow-theme-lg" role="menu">
          <div>
            <span className="block truncate text-theme-sm font-medium text-gray-700">{user?.fullName}</span>
            <span className="mt-0.5 block truncate text-theme-xs text-gray-500">{user?.email}</span>
          </div>
          <ul className="flex flex-col gap-1 border-b border-gray-200 pb-3 pt-4">
            {showProfile && (
              <li>
                <button onClick={() => { setOpen(false); setProfileOpen(true) }} className="dropdown-item group">
                  <UserCircle size={22} className="text-gray-500 group-hover:text-gray-700" /> Edit profile
                </button>
              </li>
            )}
            <li>
              <button onClick={() => { setOpen(false); setChangePwOpen(true) }} className="dropdown-item group">
                <KeyRound size={22} className="text-gray-500 group-hover:text-gray-700" /> Change password
              </button>
            </li>
          </ul>
          <button onClick={() => { logout(); navigate('/login') }} className="dropdown-item group mt-3">
            <LogOut size={22} className="text-gray-500 group-hover:text-gray-700" /> Sign out
          </button>
        </div>
      )}

      {profileOpen && <AccountProfileModal user={user} onClose={() => setProfileOpen(false)} />}
      {changePwOpen && <ChangePasswordModal onClose={() => setChangePwOpen(false)} />}
    </div>
  )
}
